// src/integrations/consent-mode.ts
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { ConsentState } from '../types'
import { debugLog } from '../utils/helpers'

/**
 * Google Consent Mode V2 parameters mapping
 */
export interface ConsentModeState {
  ad_storage: 'granted' | 'denied'
  analytics_storage: 'granted' | 'denied'
  ad_user_data: 'granted' | 'denied'
  ad_personalization: 'granted' | 'denied'
  functionality_storage: 'granted' | 'denied'
  personalization_storage: 'granted' | 'denied'
  security_storage: 'granted' | 'denied'
}

/**
 * Initialize gtag function if not exists
 */
function ensureGtagExists(): void {
  if (typeof window === 'undefined') return

  if (!(window as any).gtag) {
    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).gtag = function (...args: any[]) {
      ;(window as any).dataLayer.push(args)
    }
    ;(window as any).gtag('js', new Date())
    debugLog('gtag function initialized')
  }
}

/**
 * Map plugin categories to Google Consent Mode parameters
 */
export function mapCategoriesToConsentMode(consent: ConsentState): ConsentModeState {
  return {
    // Marketing category controls advertising
    ad_storage: consent['marketing'] ? 'granted' : 'denied',
    ad_user_data: consent['marketing'] ? 'granted' : 'denied',
    ad_personalization: consent['marketing'] ? 'granted' : 'denied',

    // Analytics category controls analytics
    analytics_storage: consent['analytics'] ? 'granted' : 'denied',

    // Functional category controls functionality
    functionality_storage: consent['functional'] ? 'granted' : 'denied',
    personalization_storage: consent['functional'] ? 'granted' : 'denied',

    // Security is always granted (strictly necessary)
    security_storage: 'granted',
  }
}

/**
 * Set default consent state (call BEFORE loading Google tags)
 * Default should be 'denied' for all except security_storage
 */
export function setConsentDefault(): void {
  if (typeof window === 'undefined') return

  ensureGtagExists()

  const defaultConsent: ConsentModeState = {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  }

  ;(window as any).gtag('consent', 'default', defaultConsent)

  debugLog('Consent Mode V2 default set', defaultConsent)
}

/**
 * Update consent state after user interaction
 * Call this when user accepts/declines cookies
 */
export function updateConsentMode(consent: ConsentState): void {
  if (typeof window === 'undefined') return

  ensureGtagExists()

  const consentMode = mapCategoriesToConsentMode(consent)

  ;(window as any).gtag('consent', 'update', consentMode)

  debugLog('Consent Mode V2 updated', consentMode)
}

/**
 * Set consent default for specific region
 */
export function setConsentDefaultForRegion(region: string | string[]): void {
  if (typeof window === 'undefined') return

  ensureGtagExists()

  const defaultConsent = {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    region: Array.isArray(region) ? region : [region],
  }

  ;(window as any).gtag('consent', 'default', defaultConsent)

  debugLog('Consent Mode V2 default set for region', { region, defaultConsent })
}

/**
 * Enable URL passthrough for ad click tracking when ad_storage is denied
 */
export function enableUrlPassthrough(): void {
  if (typeof window === 'undefined') return

  ensureGtagExists()
  ;(window as any).gtag('set', 'url_passthrough', true)

  debugLog('URL passthrough enabled')
}

/**
 * Enable ads data redaction when ad_storage is denied
 */
export function enableAdsDataRedaction(): void {
  if (typeof window === 'undefined') return

  ensureGtagExists()
  ;(window as any).gtag('set', 'ads_data_redaction', true)

  debugLog('Ads data redaction enabled')
}

/**
 * Initialize Consent Mode with best practices
 * Call this BEFORE any Google tags are loaded
 */
export function initConsentMode(options?: {
  region?: string | string[]
  urlPassthrough?: boolean
  adsDataRedaction?: boolean
}): void {
  if (typeof window === 'undefined') return

  // Set default consent
  if (options?.region) {
    setConsentDefaultForRegion(options.region)
  } else {
    setConsentDefault()
  }

  // Optional features
  if (options?.urlPassthrough) {
    enableUrlPassthrough()
  }

  if (options?.adsDataRedaction) {
    enableAdsDataRedaction()
  }

  debugLog('Consent Mode initialized with options', options)
}
